import { useState } from "react";
import { Bell, BellSlash, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

export default function PushToggle({ compact = false }) {
  const { supported, permission, subscription, busy, enable, disable, sendTest } = usePushNotifications();
  const [testing, setTesting] = useState(false);

  if (!supported) return null;

  const isOn = !!subscription && permission === "granted";

  const handleClick = async () => {
    if (isOn) {
      const r = await disable();
      if (r.ok) toast.success("Notifications disabled");
      else toast.error(r.error || "Failed to disable");
      return;
    }
    const r = await enable();
    if (r.ok) toast.success("Notifications enabled");
    else toast.error(r.error || "Permission required");
  };

  const handleTest = async () => {
    setTesting(true);
    const r = await sendTest();
    setTesting(false);
    if (r.ok) toast.success(`Test sent (${r.delivered} device${r.delivered === 1 ? "" : "s"})`);
    else toast.error(r.error || "Test failed");
  };

  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={busy}
        data-testid="push-toggle-compact"
        title={isOn ? "Push notifications ON — click to disable" : "Enable push notifications"}
        className={`w-9 h-9 border flex items-center justify-center transition-colors ${
          isOn
            ? "border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00]/10"
            : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00] hover:text-[#ff8c00]"
        } ${busy ? "opacity-50" : ""}`}
      >
        {busy ? <ArrowsClockwise size={14} className="animate-spin" /> : isOn ? <Bell size={14} weight="fill" /> : <BellSlash size={14} />}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleClick}
        disabled={busy}
        data-testid="push-toggle"
        className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors ${
          isOn
            ? "border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00]/10"
            : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#ff8c00] hover:text-[#ff8c00]"
        }`}
      >
        {busy ? <ArrowsClockwise size={12} className="animate-spin" /> : isOn ? <Bell size={12} weight="fill" /> : <BellSlash size={12} />}
        {isOn ? "Alerts On" : "Enable Alerts"}
      </button>
      {isOn && (
        <button
          onClick={handleTest}
          disabled={testing}
          data-testid="push-test"
          className="px-2 py-1.5 border border-[#2a2a2a] text-[#a1a1aa] hover:border-[#4682b4] hover:text-[#4682b4] text-[10px] uppercase tracking-[0.2em] font-bold"
        >
          {testing ? "…" : "Test"}
        </button>
      )}
    </div>
  );
}
